import type { User, UserRole } from '../modules/platform/types';
import { ROUTES, USER_ROLES } from './constants';

/** Query keys for LMS launch JWT (priority: token, assertion_jwt, assertion). */
export const LMS_ASSERTION_PARAM_KEYS = ['token', 'assertion_jwt', 'assertion'] as const;

/**
 * Extract assertion JWT from URL search params only (never from hash or logs).
 * Primary: /lms/launch?token=... ; legacy: assertion_jwt, assertion
 */
export function extractAssertionJwt(searchParams: URLSearchParams): string | null {
  for (const key of LMS_ASSERTION_PARAM_KEYS) {
    const raw = searchParams.get(key);
    if (raw && raw.trim()) {
      return raw.trim();
    }
  }
  return null;
}

/**
 * Prevent open redirects: only same-origin relative paths.
 */
export function resolveSafeReturnUrl(returnUrl: string | null | undefined, fallback: string): string {
  if (!returnUrl || !returnUrl.trim()) {
    return fallback;
  }
  const trimmed = returnUrl.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return fallback;
  }
  const lower = trimmed.toLowerCase();
  if (lower.includes('://') || lower.startsWith('javascript:') || lower.startsWith('data:')) {
    return fallback;
  }
  try {
    const parsed = new URL(trimmed, 'https://localhost');
    if (parsed.origin !== 'https://localhost') {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function getDefaultRouteForUser(user: User | null): string {
  if (!user) return ROUTES.LOGIN;
  if (user.roles.includes(USER_ROLES.DEV_TEAM)) return ROUTES.DEV_DASHBOARD;
  if (user.roles.includes(USER_ROLES.SCHOOL_ADMIN)) return ROUTES.ADMIN_DASHBOARD;
  if (user.roles.includes(USER_ROLES.TEACHER)) return ROUTES.TEACHER_DASHBOARD;
  if (user.roles.includes(USER_ROLES.STUDENT)) return ROUTES.STUDENT_DASHBOARD;
  if (user.roles.includes(USER_ROLES.PARENT)) return ROUTES.PARENT_DASHBOARD;
  return ROUTES.LOGIN;
}

/** Default post-LMS-launch route; students land on AI Tutor live. */
/** Roles from `user.roles`, or membership roles when the API omits top-level roles. */
export function getEffectiveUserRoles(user: User | null): UserRole[] {
  if (!user) return [];
  if (user.roles.length > 0) return [...user.roles];
  const fromMemberships = (user.memberships ?? [])
    .map((m) => m.role)
    .filter((role): role is UserRole => !!role);
  return [...new Set(fromMemberships)];
}

export function userHasStudentRole(user: User | null): boolean {
  return getEffectiveUserRoles(user).includes(USER_ROLES.STUDENT);
}

export function getLmsDefaultRouteForUser(user: User | null): string {
  if (!user) return ROUTES.LOGIN;
  if (userHasStudentRole(user)) return ROUTES.STUDENT_TAMIL_TUTOR;
  return getDefaultRouteForUser(user);
}

export function isStudentUser(user: User | null): boolean {
  return userHasStudentRole(user);
}

/** LMS student deep links that may be preserved on launch (query/hash kept). */
const LMS_STUDENT_ALLOWED_RETURN_BASES = [
  ROUTES.STUDENT_TAMIL_TUTOR,
  ROUTES.STUDENT_COMMUNICATION_TUTOR,
  ROUTES.STUDENT_SCENARIO_CONVERSATION,
] as const;

function isAllowedLmsStudentReturnPath(path: string): boolean {
  return LMS_STUDENT_ALLOWED_RETURN_BASES.some(
    (base) =>
      path === base ||
      path.startsWith(`${base}?`) ||
      path.startsWith(`${base}#`) ||
      path.startsWith(`${base}/`)
  );
}

/**
 * LMS launch landing path. Students default to AI Tutor live unless returnUrl
 * already targets one of the three LMS features (query/hash preserved).
 */
export function resolveLmsLaunchTargetPath(user: User, returnUrl?: string | null): string {
  const fallback = getLmsDefaultRouteForUser(user);
  if (!isStudentUser(user)) {
    return resolveSafeReturnUrl(returnUrl ?? null, fallback);
  }
  const safeReturnUrl = resolveSafeReturnUrl(returnUrl ?? null, fallback);
  if (isAllowedLmsStudentReturnPath(safeReturnUrl)) {
    return safeReturnUrl;
  }
  return fallback;
}

/**
 * iframe-safe navigation: use in-app router paths only (no window.top unless same app).
 */
export function shouldUseFullPageNavigation(_targetPath: string): boolean {
  return false;
}
